import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { ShieldCheck, Zap, Globe } from 'lucide-react';
import { AnalyticsData } from '../types/License';
import dashboardApi from '../api/dashboardApi';
import './MonitoringAnalyticsPanel.css';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="map-tooltip">
      <p className="map-tooltip-label">{label}</p>
      {payload.map((entry: any, index: number) => (
        <div key={index} className="map-tooltip-row">
          <span className="map-tooltip-dot" style={{ background: entry.color }} />
          <span>{entry.name}:</span>
          <strong>{entry.value}</strong>
        </div>
      ))}
    </div>
  );
};

const MonitoringAnalyticsPanel: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loginStats, setLoginStats] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [analyticsRes, loginRes] = await Promise.allSettled([
          dashboardApi.getAnalytics(),
          dashboardApi.getLoginAuditStats(),
        ]);
        if (!mounted) return;
        if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value);
        if (loginRes.status === 'fulfilled') setLoginStats(loginRes.value);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div className="monitor-analytics-panel">
        <p className="monitor-analytics-loading">Loading insights…</p>
      </div>
    );
  }

  const loginSuccessData = loginStats
    ? [
        { name: 'Success', value: loginStats.successful_logins, color: '#10b981' },
        { name: 'Failed', value: loginStats.failed_logins, color: '#ef4444' },
      ]
    : [];

  const dailyLoginsData =
    loginStats?.daily_logins?.map((item: any) => ({
      date: new Date(item._id).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      success: item.successful,
      failed: item.failed,
    })) || [];

  const geoData = (analytics?.geographic_distribution || []).slice(0, 6).map((item) => ({
    location: item._id
      ? item._id.length > 14
        ? `${item._id.substring(0, 14)}…`
        : item._id
      : 'Unknown',
    count: item.count || 0,
  }));

  return (
    <div className={`monitor-analytics-panel ${compact ? 'monitor-analytics-panel--compact' : ''}`}>
      {!compact && (
        <h2 className="monitor-analytics-heading">
          <ShieldCheck size={18} />
          Security &amp; Usage
        </h2>
      )}

      <div className={compact ? 'map-cards-row' : undefined}>
      <div className="map-card">
        <div className="map-card-head">
          <ShieldCheck size={16} />
          <div>
            <h3>Auth Success Rate</h3>
            {!compact && <p>Login attempts breakdown</p>}
          </div>
        </div>
        <div className="map-chart map-chart--pie">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={loginSuccessData}
                cx="50%"
                cy="50%"
                innerRadius={42}
                outerRadius={62}
                paddingAngle={3}
                dataKey="value"
                stroke="none"
              >
                {loginSuccessData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="map-pie-center">
            <span>{loginStats?.success_rate || 0}%</span>
            <small>Success</small>
          </div>
        </div>
        <div className="map-legend">
          <span><i className="map-dot map-dot--ok" /> Success: {loginStats?.successful_logins || 0}</span>
          <span><i className="map-dot map-dot--bad" /> Failed: {loginStats?.failed_logins || 0}</span>
        </div>
      </div>

      <div className="map-card">
        <div className="map-card-head">
          <Zap size={16} />
          <div>
            <h3>Daily Auth Activity</h3>
            {!compact && <p>Success vs failed logins</p>}
          </div>
        </div>
        <div className="map-chart map-chart--bar">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailyLoginsData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} width={28} />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" align="right" iconType="circle" height={24} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="success" name="Success" fill="#10b981" radius={[3, 3, 0, 0]} barSize={10} />
              <Bar dataKey="failed" name="Failed" fill="#ef4444" radius={[3, 3, 0, 0]} barSize={10} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="map-card">
        <div className="map-card-head">
          <Globe size={16} />
          <div>
            <h3>Geographic Distribution</h3>
            {!compact && <p>Top locations by usage</p>}
          </div>
        </div>
        <div className="map-chart map-chart--bar">
          {geoData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={geoData} layout="vertical" margin={{ left: 4, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} stroke="#f1f5f9" />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} />
                <YAxis
                  dataKey="location"
                  type="category"
                  width={72}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 10 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#06b6d4" radius={[0, 4, 4, 0]} barSize={12} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="map-empty">
              <Globe size={32} strokeWidth={1.5} />
              <p>No location data yet</p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default MonitoringAnalyticsPanel;
